import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { DateRangePicker } from 'react-dates';
import './supChatBot.css';
import { supChatBotSendMessageAll, supChatBotUsersFun, supportDropdown } from '../../api/supportAdmin';
import ReactLoading from 'react-loading';
import Pagination from '../../components/pagination/Pagination';
import EmptyPage from '../../components/empty-page/emptyPage';
import Instagram from '../../assets/icons/trvto_instagram.svg';
import Whatsapp from '../../assets/icons/trvto_whatsapp.svg';
import { ReactComponent as DateCalendar } from '../../assets/icons/trvto_dateCalendar.svg';
import LeadDetailsDrawer from './LeadDetailsDrawer/LeadDetailsDrawer';
import { timeAgo } from '../../utils/getTime';
import { formatPhone } from '../../utils/formatPhoneNumber';
import SendMessageModal from './sendMessage/SendMessageModal';
import { toast } from 'react-toastify';
import { getChatTimeLeft } from '../../utils/getChatTimeLeft';
import socket from '../../utils/socket';
import ChatBotDataModal from './chatBotData/ChatBotDataModal';
import ChatBotFaqModal from './chatBotFaq/ChatBotFaqModal';
import AddLead from './AddLead/AddLead';
import { getAssignableSupUsers } from '../../api/superAdmin';
import useHotLeadAlerts from './useHotLeadAlerts';
import ChatbotReportModal from './report/ChatbotReportModal';
import scoreStyles from './LeadScore.module.css';


// How serious the lead is (set by the bot from customer replies)
const INTEREST_LABELS = {
    hot: '🔥 Hot',
    warm: 'Warm',
    cold: 'No reply',
    not_interested: 'Not interested'
};

// When they plan to travel (asked by the bot)
const TRAVEL_LABELS = {
    weekend: '📅 This weekend',
    month: '🗓️ This month'
};

const TravelBadge = ({ window }) =>
    TRAVEL_LABELS[window] ? <span className='cbuTravelBadge'>{TRAVEL_LABELS[window]}</span> : null;

// Lead score 0-100 (chat + website activity); hover shows why
const LeadScoreBadge = ({ row }) => {
    const score = Number(row?.leadScore) || 0;
    if (score <= 0) return null;
    const tone = score >= 100 ? 'booked' : score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'low';
    return (
        <span className={`${scoreStyles.score} ${scoreStyles[tone]}`} title={(row?.leadScoreReasons || []).join(' · ')}>
            {score >= 100 ? '✓ Booked' : `${score} pts`}
        </span>
    );
};

const InterestBadge = ({ level }) => {
    if (!level || !INTEREST_LABELS[level]) return null;
    return <span className={`cbuInterestBadge cbuInterest-${level}`}>{INTEREST_LABELS[level]}</span>;
};

export default function ChatbotUsers() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [users, setUsers] = useState([]);
    const usersRef = useRef([]);
    const fetchUsersRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const limit = Number(searchParams.get('limit')) || 10;
    const [offset, setOffset] = useState(Number(searchParams.get('offset')) || 0);
    const [focusedInput, setFocusedInput] = useState(null);
    const [platform, setPlatform] = useState(searchParams.get('platform') || '');
    const [fromDate, setFromDate] = useState(
        searchParams.get('fromDate') ? moment(searchParams.get('fromDate')) : null
    );
    const [open, setOpen] = useState(false);
    const [openChatBotData, setOpenChatBotData] = useState(false);
    const [openChatBotFaq, setOpenChatBotFaq] = useState(false);
    const [openLeadModal, setOpenLeadModal] = useState(false);
    const [toDate, setToDate] = useState(searchParams.get('toDate') ? moment(searchParams.get('toDate')) : null);
    const [day, setDay] = useState(searchParams.get('day') || '');
    const [search, setSearch] = useState('');
    const pageNumber = Math.floor(offset / limit) + 1;
    const [totalCount, setTotalCount] = useState(0);
    const [whatsappUsers, setWhatsappUsers] = useState(0);
    const [instagramUsers, setInstagramUsers] = useState(0);
    const [hotLeads, setHotLeads] = useState(0);
    // WhatsApp-style "Unread" filter
    const [unreadOnly, setUnreadOnly] = useState(false);
    // Sort by lead score (most likely to book first)
    const [sortByScore, setSortByScore] = useState(false);
    const [unreadChats, setUnreadChats] = useState(0);
    const unreadOnlyRef = useRef(false);
    const openChatIdRef = useRef(null);
    const [interest, setInterest] = useState('');
    const [travel, setTravel] = useState('');
    const [showReport, setShowReport] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [sendModal, setSendModal] = useState(false);
    const [message, setMessage] = useState('');
    const [sendType, setSendType] = useState('selected');
    const [broadcastFilters, setBroadcastFilters] = useState({
        leadStatus: [],
        window: 'active'
    });

    const [filteredUserCount, setFilteredUserCount] = useState(0);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({ field: 'lastInteractionAt', direction: 'desc' });
    const [statusDropdown, setStatusDropdown] = useState([]);
    const [leadStatus, setLeadStatus] = useState('');
    const [assignedTo, setAssignedTo] = useState('');
    const [supportUsers, setSupportUsers] = useState([]);
    const isSuperAdmin = localStorage.getItem('supAccess') === 'superAdmin';

    const calendarIcon = (
        <span role='img' aria-label='calendar' className='calendar-icon'>
            <DateCalendar />
        </span>
    );

    useEffect(() => {
        usersRef.current = users;
    }, [users]);

    useEffect(() => {
        unreadOnlyRef.current = unreadOnly;
    }, [unreadOnly]);

    useEffect(() => {
        openChatIdRef.current = open ? selectedUser?._id : null;
    }, [open, selectedUser?._id]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await supportDropdown(10, 0, 'trip', 'active');
                const status = data.filter((item) => item.heading === 'Status');
                setStatusDropdown(status?.[0].dropdown);
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isSuperAdmin) return;

        fetchAssignableUsers();
    }, [isSuperAdmin]);

    const fetchAssignableUsers = async () => {
        try {
            const result = await getAssignableSupUsers();

            if (result.status === 200) {
                setSupportUsers(result.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching assignable users:', error);
        }
    };

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);

            const { data } = await supChatBotUsersFun({
                platform,
                fromDate: fromDate?.format('YYYY-MM-DD'),
                toDate: toDate?.format('YYYY-MM-DD'),
                search,
                limit,
                offset,
                leadStatus,
                assignedSupportId: assignedTo,
                interest,
                travel,
                unread: unreadOnly ? 'true' : undefined,
                sort: sortByScore ? 'score' : undefined
            });

            setUsers(data?.users || []);
            setTotalCount(data?.totalCount || 0);
            setWhatsappUsers(data?.whatsappUsers || 0);
            setInstagramUsers(data?.instagramUsers || 0);
            setHotLeads(data?.hotLeads || 0);
            setUnreadChats(data?.unreadChats || 0);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }, [platform, fromDate, toDate, search, limit, offset, leadStatus, assignedTo, interest, travel, unreadOnly, sortByScore]);

    useEffect(() => {
        fetchUsersRef.current = fetchUsers;
    }, [fetchUsers]);

    useEffect(() => {
        if (!sendModal || sendType !== 'filter') {
            return;
        }

        if (!broadcastFilters.leadStatus?.length) {
            setFilteredUserCount(0);
            return;
        }

        const getFilteredUserCount = async () => {
            try {
                const { data } = await supChatBotUsersFun({
                    platform: '',
                    fromDate: '',
                    toDate: '',
                    search: '',
                    limit: 1,
                    offset: 0,
                    leadStatus: broadcastFilters.leadStatus,
                    window: broadcastFilters.window
                });

                setFilteredUserCount(data?.totalCount || 0);
            } catch (error) {
                console.error('Failed to get broadcast count:', error);
                setFilteredUserCount(0);
            }
        };

        getFilteredUserCount();
    }, [sendModal, sendType, broadcastFilters]);

    useEffect(() => {
        const handleChatUpdated = (data) => {
            if (!data?.chatId || !data?.user) return;

            const exists = usersRef.current.some((user) => user._id === data.chatId);

            if (!exists) {
                // New user
                fetchUsersRef.current?.();
                return;
            }

            // Existing user — keep the Unread count in step when a chat
            // becomes read / gets a new unread message
            const before = usersRef.current.find((user) => user._id === data.chatId);
            const wasUnread = (before?.unreadCount || 0) > 0;
            const isUnread = (data.user.unreadCount ?? before?.unreadCount ?? 0) > 0;
            if (wasUnread !== isUnread) {
                setUnreadChats((count) => Math.max(0, count + (isUnread ? 1 : -1)));
            }

            setUsers((prev) =>
                prev
                    .map((user) => (user._id === data.chatId ? { ...user, ...data.user } : user))
                    // In the Unread view, drop chats once they're read (but not
                    // the one that is open right now)
                    .filter(
                        (user) =>
                            !unreadOnlyRef.current ||
                            user.unreadCount > 0 ||
                            user._id === openChatIdRef.current
                    )
                    .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt))
            );

            setSelectedUser((prev) => {
                if (!prev || prev._id !== data.chatId) {
                    return prev;
                }

                return {
                    ...prev,
                    ...data.user
                };
            });
        };

        socket.on('chat-updated', handleChatUpdated);

        return () => {
            socket.off('chat-updated', handleChatUpdated);
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers();
        }, 400);

        return () => clearTimeout(timer);
    }, [fetchUsers]);

    useEffect(() => {
        const params = {};

        if (platform) params.platform = platform;

        if (fromDate) params.fromDate = fromDate.format('YYYY-MM-DD');

        if (toDate) params.toDate = toDate.format('YYYY-MM-DD');

        params.limit = limit;
        params.offset = offset;

        setSearchParams(params);
    }, [platform, fromDate, toDate, offset]);

    const paginationClick = (number, offSetNumber) => {
        setOffset(+offSetNumber);

        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const getProgress = (user) => {
        if (user?.selectedPlace) return 100;
        if (user?.selectedType) return 75;
        if (user?.selectedCity) return 50;
        return 25;
    };

    const getStatus = (user) => {
        if (user?.selectedPlace) return 'Place Selected';
        if (user?.selectedType) return 'Type Selected';
        if (user?.selectedCity) return 'City Selected';
        return 'Started';
    };

    const getFollowUps = (user) => {
        return [
            user?.followUp1Sent,
            user?.followUp2Sent,
            user?.followUp3Sent,
            user?.followUp4Sent,
            user?.followUp5Sent
        ].filter(Boolean).length;
    };

    const clearFilters = () => {
        setFromDate(null);
        setToDate(null);
        setPlatform('');
        setLeadStatus('');
        setInterest('');
        setTravel('');
        setUnreadOnly(false);
    };

    // const filteredUsers = users.filter((user) => {
    //     const value = search.toLowerCase();

    //     return (
    //         user?.name?.toLowerCase().includes(value) ||
    //         user?.leadStatus?.toLowerCase().includes(value) ||
    //         user?.phoneNumber?.toLowerCase().includes(value) ||
    //         user?.selectedCity?.toLowerCase().includes(value) ||
    //         user?.selectedType?.toLowerCase().includes(value) ||
    //         user?.selectedPlace?.toLowerCase().includes(value) ||
    //         user?.platform?.toLowerCase().includes(value)
    //     );
    // });

    // 2. Sort handler — click same field to flip direction, click new field to sort desc first
    const handleSort = (field) => {
        setSortConfig((prev) => {
            if (prev.field === field) {
                return { field, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
            }
            return { field, direction: 'desc' };
        });
    };

    // 3. Sortable field config — maps header -> value getter (handles nested/derived values)
    const sortValueGetters = {
        leadStatus: (row) => row?.leadStatus || '',
        travellerCount: (row) => row?.travellerCount ?? -1,
        lastInteractionAt: (row) => new Date(row?.lastInteractionAt).getTime(),
        followUps: (row) => getFollowUps(row)
    };

    // 4. Apply sort AFTER your existing filter, before render
    const sortedUsers = [...users].sort((a, b) => {
        const getValue = sortValueGetters[sortConfig.field];
        if (!getValue) return 0;

        const valA = getValue(a);
        const valB = getValue(b);

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const SortableHeader = ({ field, label }) => {
        const isActive = sortConfig.field === field;
        return (
            <th onClick={() => handleSort(field)} className='sortableHeader'>
                {label}
                <span className={`sortIcon ${isActive ? 'sortIconActive' : ''}`}>
                    {isActive ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
                </span>
            </th>
        );
    };

    const handleSelect = (id) => {
        setSelectedUsers((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const handleSelectAll = () => {
        if (selectedUsers.length === users.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map((u) => u._id));
        }
    };

    const sendBroadcast = async () => {
        try {
            if (!message.trim()) {
                toast.error('Please enter a message.');
                return;
            }

            if (sendType === 'selected' && selectedUsers.length === 0) {
                toast.error('Please select at least one user.');
                return;
            }

            if (sendType === 'filter' && broadcastFilters.leadStatus.length === 0) {
                toast.error('Please select at least one lead status.');
                return;
            }
            setLoading(true);
            const payload = {
                type: sendType,
                message,
                users: sendType === 'selected' ? selectedUsers : [],
                filters: sendType === 'filter' ? broadcastFilters : {}
            };

            const response = await supChatBotSendMessageAll(payload);

            if (response.status === 200) {
                toast.success(`Message sent to ${response.data?.sent || 0} users`);
                setLoading(false);
                setSendModal(false);
                setSelectedUsers([]);
                setMessage('');
                setSendType('selected');
                setBroadcastFilters({});
            }
        } catch (err) {
            console.log(err);
            setLoading(false);
            toast.error(err?.response?.data?.message || 'Failed to send message');
        }
    };

    // 🔥 Hot-lead alert: sound + toast + desktop notification.
    // Clicking it opens the lead if it's on the current page, otherwise
    // shows the Hot Leads list.
    const openLeadById = useCallback(async (chatId) => {
        let row = usersRef.current?.find((u) => u._id === chatId);
        if (!row) {
            // Not on this page — load that one chat directly
            try {
                const { data } = await supChatBotUsersFun({ id: chatId, limit: 1 });
                row = data?.users?.[0];
            } catch (error) {
                console.log(error);
            }
        }
        if (row) {
            setSelectedUser(row);
            setOpen(true);
        } else {
            setInterest('hot');
            setOffset(0);
        }
    }, []);
    useHotLeadAlerts(openLeadById);

    const openUser = (row) => {
        setSelectedUser(row);
        setOpen(true);
    };

    return (
        <>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}

            <div className='chatbotDashboard'>
                <div className='dashboardHeader'>
                    <h2>Chatbot Users</h2>
                    <div className='botButtons'>
                        {!selectedUsers.length > 0 && (
                            <button className='sendBtn' onClick={() => setSendModal(true)}>
                                Send Message
                            </button>
                        )}

                        <button
                            className='sendBtn'
                            onClick={() => setOpenLeadModal(true)}
                            style={{ padding: '0px 12px' }}
                        >
                            <span style={{ fontSize: '25px', paddingBottom: '7px' }}>+</span> Add Lead
                        </button>
                        <button className='sendBtn' onClick={() => setOpenChatBotData(true)}>
                            Manage Chatbot Data
                        </button>
                        <button className='sendBtn' onClick={() => setOpenChatBotFaq(true)}>
                            Q&amp;A Replies
                        </button>
                        <button className='sendBtn' onClick={() => setShowReport(true)}>
                            📊 Report
                        </button>
                    </div>
                </div>

                <div className='toolbar'>
                    <div className='statsRow'>
                        <div className='statCard'>
                            <h4>Total Users</h4>
                            <h2>{totalCount}</h2>
                        </div>

                        <div className='statCard'>
                            <h4>WhatsApp Users</h4>
                            <h2>{whatsappUsers}</h2>
                        </div>

                        <div className='statCard'>
                            <h4>Instagram Users</h4>
                            <h2>{instagramUsers}</h2>
                        </div>

                        <div
                            className={`statCard hotLeadsCard ${interest === 'hot' ? 'hotLeadsCardActive' : ''}`}
                            title='Customers who asked for a call or want to book'
                            onClick={() => {
                                setInterest((prev) => (prev === 'hot' ? '' : 'hot'));
                                setOffset(0);
                            }}
                        >
                            <h4>🔥 Hot Leads</h4>
                            <h2>{hotLeads}</h2>
                        </div>
                    </div>

                    {!isSuperAdmin && (
                        <input
                            className='searchInput'
                            placeholder='Search user'
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setOffset(0);
                            }}
                        />
                    )}

                    {/* Mobile-only trigger that reveals the filter row below */}
                    <button className='filterToggleBtn' onClick={() => setFiltersOpen((prev) => !prev)}>
                        Filters {filtersOpen ? '▲' : '▼'}
                    </button>

                    <div
                        className={`${isSuperAdmin ? 'filterGroupSupUser' : 'filterGroup'} ${filtersOpen ? 'filterGroupOpen' : ''}`}
                    >
                        {isSuperAdmin && (
                            <input
                                className='searchInput'
                                placeholder='Search user'
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setOffset(0);
                                }}
                            />
                        )}
                        <select
                            value={platform}
                            onChange={(e) => {
                                setPlatform(e.target.value);
                                setOffset(0);
                            }}
                        >
                            <option value=''>All Platforms</option>
                            <option value='whatsapp'>WhatsApp</option>
                            <option value='instagram'>Instagram</option>
                        </select>
                        <select
                            value={leadStatus}
                            onChange={(e) => {
                                setLeadStatus(e.target.value);
                                setOffset(0);
                            }}
                        >
                            <option value=''>All Status</option>

                            {statusDropdown?.map((dropdown) => (
                                <option key={dropdown._id || dropdown.name} value={dropdown.name}>
                                    {dropdown.displayName}
                                </option>
                            ))}
                        </select>
                        <select
                            value={interest}
                            onChange={(e) => {
                                setInterest(e.target.value);
                                setOffset(0);
                            }}
                        >
                            <option value=''>All Interest</option>
                            <option value='hot'>🔥 Hot</option>
                            <option value='warm'>Warm</option>
                            <option value='cold'>Cold (no reply)</option>
                            <option value='not_interested'>Not interested</option>
                            <option value='unknown'>New / Unknown</option>
                            <option value='active'>Hide not interested</option>
                        </select>
                        <select
                            value={travel}
                            onChange={(e) => {
                                setTravel(e.target.value);
                                setOffset(0);
                            }}
                        >
                            <option value=''>Any travel date</option>
                            <option value='soon'>Travelling soon</option>
                            <option value='weekend'>This weekend</option>
                            <option value='month'>This month</option>
                            <option value='exploring'>Just exploring</option>
                        </select>
                        {isSuperAdmin && (
                            <select
                                value={assignedTo}
                                onChange={(e) => {
                                    setAssignedTo(e.target.value);
                                    setOffset(0);
                                }}
                            >
                                <option value=''>All</option>

                                {supportUsers?.map((dropdown) => (
                                    <option key={dropdown._id || dropdown.name} value={dropdown._id}>
                                        {dropdown.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        <DateRangePicker
                            startDate={fromDate}
                            startDateId='notificationStartDate'
                            endDate={toDate}
                            endDateId='notificationEndDate'
                            focusedInput={focusedInput}
                            onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                            onDatesChange={({ startDate, endDate }) => {
                                setFromDate(startDate);
                                setToDate(endDate);
                            }}
                            startDatePlaceholderText='From'
                            endDatePlaceholderText='To'
                            displayFormat='DD/MM/YYYY'
                            noBorder
                            showDefaultInputIcon
                            customArrowIcon={calendarIcon}
                            orientation={isMobile ? 'vertical' : 'horizontal'}
                            isOutsideRange={() => false}
                        />

                        <div className='clear-btn' onClick={clearFilters}>
                            Reset
                        </div>
                    </div>
                </div>

                {/* WhatsApp-style quick filter */}
                <div className='cbuChatFilter'>
                    <button
                        type='button'
                        className={`cbuChatFilterChip ${!unreadOnly ? 'active' : ''}`}
                        onClick={() => {
                            setUnreadOnly(false);
                            setOffset(0);
                        }}
                    >
                        All
                    </button>
                    <button
                        type='button'
                        className={`cbuChatFilterChip ${unreadOnly ? 'active' : ''}`}
                        onClick={() => {
                            setUnreadOnly(true);
                            setOffset(0);
                        }}
                    >
                        Unread
                        {unreadChats > 0 && <span className='cbuChatFilterCount'>{unreadChats}</span>}
                    </button>
                    <button
                        type='button'
                        className={`cbuChatFilterChip ${sortByScore ? 'active' : ''}`}
                        title='Sort by lead score: chat answers + what they did on the website'
                        onClick={() => {
                            setSortByScore((v) => !v);
                            setOffset(0);
                        }}
                    >
                        🎯 Most likely to book
                    </button>
                </div>

                {users?.length === 0 ? (
                    unreadOnly ? (
                        <p className='cbuNoUnread'>No unread chats 🎉</p>
                    ) : (
                        <EmptyPage />
                    )
                ) : (
                    <>
                        {selectedUsers.length > 0 && (
                            <div className='bulkActionBar'>
                                <div className='bulkActionLeft'>
                                    <span>
                                        <b>{selectedUsers.length}</b> users selected
                                    </span>
                                    <button className='clear-btn' onClick={() => setSelectedUsers([])}>
                                        Clear
                                    </button>
                                </div>

                                <div>
                                    <button className='sendBtn' onClick={() => setSendModal(true)}>
                                        Send Message
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ================= DESKTOP TABLE ================= */}
                        <div className='modernTableWrapper desktopOnly'>
                            <table className='modernTable'>
                                <thead>
                                    <tr>
                                        <th className='checkboxCell'>
                                            <input
                                                type='checkbox'
                                                checked={selectedUsers.length === users.length && users.length > 0}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th>User</th>
                                        <th>Platform</th>
                                        <SortableHeader field='leadStatus' label='Lead Status' />
                                        <SortableHeader field='travellerCount' label='Seats' />
                                        <th>24hr Window</th>
                                        {/* <SortableHeader field='lastInteractionAt' label='Last Interaction' /> */}
                                        {isSuperAdmin && <th>Assigned To</th>}
                                        <th>Selections</th>
                                        <th>Call Back</th>
                                        {/* <SortableHeader field='followUps' label='Follow Ups' /> */}
                                        <th>Bot Reply</th>
                                        <th>Comments</th>
                                        <th className='actionCell'>Action</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {sortedUsers.map((row) => (
                                        <tr key={row._id} onClick={() => openUser(row)}>
                                            <td className='checkboxCell' onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type='checkbox'
                                                    checked={selectedUsers.includes(row._id)}
                                                    onChange={() => handleSelect(row._id)}
                                                />
                                            </td>
                                            <td>
                                                <div className='userInfo'>
                                                    <div className='avatar'>{(row?.name || 'U')[0]}</div>

                                                    <div>
                                                        <h5>{row?.name || 'Anonymous'}</h5>

                                                        <p style={{ textWrap: 'nowrap' }}>
                                                            {row?.platform === 'instagram'
                                                                ? formatPhone(row?.phoneNumber) || row?.igUserId
                                                                : formatPhone(row?.phoneNumber)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className='platform-icon'>
                                                    <img
                                                        src={row?.platform === 'instagram' ? Instagram : Whatsapp}
                                                        width={22}
                                                        alt={row?.platform}
                                                    />

                                                    {row?.unreadCount > 0 && (
                                                        <span className='badge'>{row.unreadCount}</span>
                                                    )}
                                                </div>
                                            </td>

                                            <td>
                                                <span
                                                    className={`leadStatusBadge ${row?.leadStatus.replace(/\s/g, '')}`}
                                                >
                                                    {row?.leadStatus}
                                                </span>
                                                <InterestBadge level={row?.interestLevel} />
                                                <LeadScoreBadge row={row} />
                                                <TravelBadge window={row?.travelWindow} />
                                            </td>
                                            <td>{row.travellerCount}</td>
                                            <td>
                                                {(() => {
                                                    const win = getChatTimeLeft(row.lastInteractionAt);

                                                    return (
                                                        <span
                                                            className={win.expired ? 'windowExpired' : 'windowActive'}
                                                        >
                                                            {win.text}
                                                        </span>
                                                    );
                                                })()}
                                            </td>

                                            {isSuperAdmin && <td>{row?.assignedSupportId?.name || '-'}</td>}

                                            <td>
                                                <div className='selectionns'>
                                                    <span title={row.selectedCity}>{row.selectedCity || '-'}</span>
                                                    <span title={row.selectedType}>{row.selectedType || '-'}</span>
                                                    <span title={row.selectedPlace}>{row.selectedPlace || '-'}</span>
                                                </div>
                                            </td>
                                            <td>{row?.supportRequested === true ? 'Yes' : 'No'}</td>
                                            <td className='cbuToggleCell' title={row?.isAgentHandling ? 'Agent is replying' : 'Bot is replying'}>
                                                <label className='cbuToggle'>
                                                    <input
                                                        type='checkbox'
                                                        readOnly
                                                        checked={!row?.isAgentHandling}
                                                        // onChange={(e) => {
                                                        //     e.stopPropagation();
                                                        //     updateUserData({
                                                        //         updates: {
                                                        //             isAgentHandling: !e.target.checked
                                                        //         }
                                                        //     });
                                                        // }}
                                                    />
                                                    <span className='cbuToggleTrack' />
                                                    <span className='cbuToggleThumb' />
                                                </label>
                                            </td>
                                            {/* <td>
                                                {getFollowUps(row)}
                                                /5
                                            </td> */}
                                            <td className='commentCell'>
                                                {row.comments.length === 0 ? (
                                                    <span>No comments yet</span>
                                                ) : (
                                                    row.comments.slice(0, 1).map((item) => (
                                                        <div className='commentItem' key={item.id}>
                                                            <p title={item.message}>{item.message}</p>
                                                            <div className='commentBy'>
                                                                <span>{item?.addedBy?.name || 'Admin'}</span>
                                                                <span>-</span>
                                                                <span>{timeAgo(item.createdAt)}</span>
                                                            </div>
                                                            <div>{row.comments.length} comments</div>
                                                        </div>
                                                    ))
                                                )}
                                            </td>
                                            <td className='actionCell'>
                                                <button className='viewBtn' onClick={() => openUser(row)}>
                                                    Manage
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* ================= MOBILE CARD LIST ================= */}
                        <div className='mobileCardList mobileOnly'>
                            <div className='mobileSelectAllRow'>
                                <label className='mobileSelectAllLabel'>
                                    <input
                                        type='checkbox'
                                        checked={selectedUsers.length === users.length && users.length > 0}
                                        onChange={handleSelectAll}
                                    />
                                    Select all ({users.length})
                                </label>
                            </div>

                            {sortedUsers.map((row) => {
                                const win = getChatTimeLeft(row.lastInteractionAt);
                                const selections = [row.selectedCity, row.selectedType, row.selectedPlace]
                                    .filter(Boolean)
                                    .join(' • ');

                                return (
                                    <div className='userCard' key={row._id} onClick={() => openUser(row)}>
                                        <div className='userCardTop'>
                                            <input
                                                type='checkbox'
                                                className='userCardCheckbox'
                                                checked={selectedUsers.includes(row._id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => handleSelect(row._id)}
                                            />

                                            <div className='avatar userCardAvatar'>{(row?.name || 'U')[0]}</div>

                                            <div className='userCardIdentity'>
                                                <h5>{row?.name || 'Anonymous'}</h5>
                                                <p>
                                                    {row?.platform === 'instagram'
                                                        ? row?.igUserId
                                                        : formatPhone(row?.phoneNumber)}
                                                </p>
                                            </div>

                                            <div className='platform-icon userCardPlatform'>
                                                <img
                                                    src={row?.platform === 'instagram' ? Instagram : Whatsapp}
                                                    width={22}
                                                    alt={row?.platform}
                                                />

                                                {row?.unreadCount > 0 && (
                                                    <span className='badge'>{row.unreadCount}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className='userCardBadges'>
                                            <span className={`leadStatusBadge ${row?.leadStatus.replace(/\s/g, '')}`}>
                                                {row?.leadStatus}
                                            </span>
                                            <InterestBadge level={row?.interestLevel} />
                                                <LeadScoreBadge row={row} />
                                            <TravelBadge window={row?.travelWindow} />

                                            <span className={win.expired ? 'windowExpired' : 'windowActive'}>
                                                {win.text}
                                            </span>
                                        </div>

                                        <div className='userCardMeta'>
                                            <div className='userCardMetaItem'>
                                                <span className='metaLabel'>Seats</span>
                                                <span className='metaValue'>{row.travellerCount ?? '-'}</span>
                                            </div>

                                            <div className='userCardMetaItem'>
                                                <span className='metaLabel'>Follow Ups</span>
                                                <span className='metaValue'>{getFollowUps(row)}/5</span>
                                            </div>

                                            {/* <div className='userCardMetaItem'>
                                                <span className='metaLabel'>Last Interaction</span>
                                                <span className='metaValue'>
                                                    {new Date(row.lastInteractionAt).toLocaleString()}
                                                </span>
                                            </div> */}
                                            <div className='userCardMetaItem'>
                                                <span className='metaLabel'>Assigned To</span>
                                                <span className='metaValue'>{row?.assignedSupportId?.name}</span>
                                            </div>
                                        </div>

                                        {selections && (
                                            <div className='userCardSelections'>
                                                <span className='metaLabel'>Selections</span>
                                                <p>{selections}</p>
                                            </div>
                                        )}

                                        <div className='userCardComment'>
                                            {row.comments.length === 0 ? (
                                                <span className='metaLabel'>No comments yet</span>
                                            ) : (
                                                <div className='commentItem'>
                                                    <p>{row.comments[0].message}</p>
                                                    <div className='commentBy'>
                                                        <span>{row.comments[0]?.addedBy?.name || 'Admin'}</span>
                                                        <span>-</span>
                                                        <span>{timeAgo(row.comments[0].createdAt)}</span>
                                                    </div>
                                                    <div>{row.comments.length} comments</div>
                                                </div>
                                            )}
                                        </div>

                                        <button className='viewBtn userCardManageBtn' onClick={() => openUser(row)}>
                                            Manage
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {sendModal && (
                    <SendMessageModal
                        open={sendModal}
                        onClose={() => setSendModal(false)}
                        selectedUsers={selectedUsers}
                        totalUsers={totalCount}
                        sendType={sendType}
                        setSendType={setSendType}
                        message={message}
                        setMessage={setMessage}
                        broadcastFilters={broadcastFilters}
                        setBroadcastFilters={setBroadcastFilters}
                        statusDropdown={statusDropdown}
                        filteredUserCount={filteredUserCount}
                        onSend={sendBroadcast}
                        loading={loading}
                    />
                )}
                <Pagination
                    paginationClick={paginationClick}
                    numberOfRows={totalCount}
                    offSet={offset}
                    pageNumber={pageNumber}
                    limit={limit}
                />
            </div>
            <ChatBotDataModal open={openChatBotData} onClose={() => setOpenChatBotData(false)} />
            <ChatBotFaqModal open={openChatBotFaq} onClose={() => setOpenChatBotFaq(false)} />
            <ChatbotReportModal
                open={showReport}
                onClose={() => setShowReport(false)}
                fromDate={fromDate?.format('YYYY-MM-DD')}
                toDate={toDate?.format('YYYY-MM-DD')}
                assignedSupportId={assignedTo || undefined}
                onOpenLead={openLeadById}
            />
            <AddLead open={openLeadModal} onClose={() => setOpenLeadModal(false)} />
            {open && (
                <LeadDetailsDrawer
                    open={open}
                    onClose={() => {
                        setOpen(false);
                        // Unread view: the chat just read drops out, like WhatsApp
                        if (unreadOnly) setUsers((prev) => prev.filter((u) => u.unreadCount > 0));
                    }}
                    user={selectedUser}
                    setSelectedUser={setSelectedUser}
                />
            )}
        </>
    );
}
